import { useCallback, useEffect, useState } from 'react';
import { toast } from '../../../../../ui/toast';
import {
  getWellnessCandidateResults,
  getWellnessCandidates,
  getWellnessConfig,
  getWellnessSummaryResults,
  saveWellnessConfig,
  sendWellnessInvitation,
} from '../../../../../utils/api_service/wellness';
import {
  WellnessCandidate,
  WellnessCandidateResult,
  WellnessConfig,
  WellnessSummaryResult,
} from '../../../../../utils/types/wellness_types';
import {
  WELLNESS_CONFIG_TABS,
  WELLNESS_RESULTS_TABS,
} from '../Constants';

type ConfigTab = (typeof WELLNESS_CONFIG_TABS)[number];
type ResultsTab = (typeof WELLNESS_RESULTS_TABS)[number];

const PAGE_SIZE = 20;

const DEFAULT_CONFIG: WellnessConfig = { dayLimit: 7, types: [], emailTemplate: '' };

// HTML equivalent of wellness_email_template.json - keep these two in sync if
// the default content ever changes, since this is what actually gets saved
// to the backend, while the JSON file is what the rich-text editor displays.
const DEFAULT_EMAIL_TEMPLATE_HTML = `<p>Dear {{applicant_name}}, </p>
<p>You have been invited to participate in an online wellness screening by {{company_name}} for the {{job_title_name}} position. </p>
<p>Please complete the assessment by {{end_of_assessment}}</p>
<p>Kindly note that the deadline is in Coordinated Universal Time (UTC). Please ensure that you take the time zone difference into account when submitting your response. </p>
<ol>
<li><p>Please open the email in a laptop. The wellness screening is not optimised on phones. </p></li>
<li><p>Press the Start Screening button </p></li>
<li><p>Completed all screening questions honestly. </p></li>
<li><p>Close the browser tab when finished</p></li>
</ol>
<p>We have created an account for you. To access the assessment, please click on the button below: <a target="_blank" rel="noopener noreferrer nofollow" href="{{wellness_url}}">Start Wellness Screening</a> If you have any questions or concerns, please don't hesitate to contact us at {{support_email}}.</p>
<p>Thank you for your interest in {{company_name}}!</p>`;

export function useWellness(jobOpeningId: string) {
  // ── Tab state ────────────────────────────────────────────────────────────
  const [mainTab, setMainTab] = useState<'Config' | 'Results'>('Config');
  const [configTab, setConfigTab] = useState<ConfigTab>('Settings');
  const [resultsTab, setResultsTab] = useState<ResultsTab>('Summary Results');

  // ── Data ────────────────────────────────────────────────────────────────
  const [wellnessConfig, setWellnessConfig] = useState<WellnessConfig>(DEFAULT_CONFIG);
  const [candidates, setCandidates] = useState<WellnessCandidate[]>([]);
  const [candidateTotalCount, setCandidateTotalCount] = useState(0);
  const [summaryResults, setSummaryResults] = useState<WellnessSummaryResult | null>(null);
  const [candidateResults, setCandidateResults] = useState<WellnessCandidateResult[]>([]);
  const [assessmentStatusFilter, setAssessmentStatusFilter] = useState('');

  // ── Pagination ───────────────────────────────────────────────────────────
  const [candidatePage, setCandidatePage] = useState(1);
  const [resultPage, setResultPage] = useState(1);

  // ── Candidate search (server-side, since candidates are paginated) ──────
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateEmailSearch, setCandidateEmailSearch] = useState('');

  // ── Loading flags ────────────────────────────────────────────────────────
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);

  // ── Fetch helpers ─────────────────────────────────────────────────────────

  const fetchConfig = useCallback(async () => {
    if (!jobOpeningId) return;
    setIsLoadingConfig(true);
    try {
      const res = await getWellnessConfig(jobOpeningId);
      if (res.ok) {
        const data = await res.json();
        setWellnessConfig({
          dayLimit: data.dayLimit ?? 0,
          types: data.types ?? [],
          emailTemplate: data.emailTemplate || DEFAULT_EMAIL_TEMPLATE_HTML,
        });
      }
    } catch (_err) {
      toast.error('Failed to load wellness settings.', { position: 'bottom-center' });
    } finally {
      setIsLoadingConfig(false);
    }
  }, [jobOpeningId]);

  const fetchCandidates = useCallback(async () => {
    if (!jobOpeningId) return;
    setIsLoadingCandidates(true);
    try {
      const res = await getWellnessCandidates(
          jobOpeningId,
          candidatePage,
          PAGE_SIZE,
          candidateSearch,
          candidateEmailSearch,
          assessmentStatusFilter,
      );
      if (res.ok) {
        const data = await res.json();
        const mapped: WellnessCandidate[] = (data.results ?? []).map((c: any) => ({
          candidateId: c.id,
          name: c.name,
          email: c.email,
          applicantStatus: c.status,
          invited: c.invited === 'Yes',
          assessmentStatus: c.assessmentStatus,
          invitationCount: c.invitationCount ?? 0,
        }));
        setCandidates(mapped);
        setCandidateTotalCount(data.total_count ?? 0);
      }
    } catch {
      toast.error('Failed to load candidates.', { position: 'bottom-center' });
    } finally {
      setIsLoadingCandidates(false);
    }
  }, [jobOpeningId, candidatePage, candidateSearch, candidateEmailSearch, assessmentStatusFilter]);

  const fetchResults = useCallback(async () => {
    if (!jobOpeningId) return;
    setIsLoadingResults(true);
    try {
      const [sumRes, candRes] = await Promise.all([
        getWellnessSummaryResults(jobOpeningId),
        getWellnessCandidateResults(jobOpeningId),
      ]);
      if (sumRes.ok) setSummaryResults(await sumRes.json());
      if (candRes.ok) setCandidateResults(await candRes.json());
    } catch {
      toast.error('Failed to load results.', { position: 'bottom-center' });
    } finally {
      setIsLoadingResults(false);
    }
  }, [jobOpeningId]);

  // ── On mount: fetch config once ────────────────────────────────────────
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // ── Candidates: (re)fetch on job change, page change, or search change ──
  useEffect(() => {
    const debounceFetch = setTimeout(() => {
      fetchCandidates();
    }, 500);
    return () => clearTimeout(debounceFetch);
  }, [fetchCandidates]);

  // reset to page 1 whenever the search terms change
  useEffect(() => {
    setCandidatePage(1);
  }, [candidateSearch, candidateEmailSearch, assessmentStatusFilter]);

  // ── Fetch results only when Results tab is active ────────────────────────
  useEffect(() => {
    if (mainTab === 'Results') fetchResults();
  }, [mainTab, fetchResults]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleMainTabChange = (tab: string) => {
    setMainTab(tab as 'Config' | 'Results');
  };

  const handleConfigTabChange = (tab: string) => {
    setConfigTab(tab as ConfigTab);
  };

  const handleResultsTabChange = (tab: string) => {
    setResultsTab(tab as ResultsTab);
  };

  const handleConfigChange = (updated: WellnessConfig) => {
    setWellnessConfig(updated);
  };

  const handleSaveConfig = useCallback(async () => {
    if (!jobOpeningId) return;
    setIsSavingConfig(true);
    try {
      const res = await saveWellnessConfig(jobOpeningId, wellnessConfig);
      if (res.ok) {
        toast.success('Wellness settings saved.', { position: 'bottom-center' });
      } else {
        toast.error('Failed to save settings.', { position: 'bottom-center' });
      }
    } catch {
      toast.error('Failed to save settings.', { position: 'bottom-center' });
    } finally {
      setIsSavingConfig(false);
    }
  }, [jobOpeningId, wellnessConfig]);

  const handleSendInvitation = useCallback(async (candidateIds: string[]) => {
    if (!jobOpeningId) return;
    setIsSendingInvitation(true);
    try {
      const res = await sendWellnessInvitation(jobOpeningId, {
        applicantIds: candidateIds,
        types: wellnessConfig.types,
      });
      if (res.ok) {
        toast.success('Invitations sent.', { position: 'bottom-center' });
        fetchCandidates(); // refresh statuses/invited flags after sending
      } else {
        toast.error('Failed to send invitations.', { position: 'bottom-center' });
      }
    } catch {
      toast.error('Failed to send invitations.', { position: 'bottom-center' });
    } finally {
      setIsSendingInvitation(false);
    }
  }, [jobOpeningId, wellnessConfig, fetchCandidates]);

  const handleSendReminder = useCallback(async (candidateId: string) => {
    await handleSendInvitation([candidateId]);
  }, [handleSendInvitation]);

  const resultTotalPages = Math.max(1, Math.ceil(candidateResults.length / PAGE_SIZE));
  const pagedResults = candidateResults.slice(
      (resultPage - 1) * PAGE_SIZE,
      resultPage * PAGE_SIZE,
  );

  return {
    // Tab state
    mainTab,
    configTab,
    resultsTab,
    handleMainTabChange,
    handleConfigTabChange,
    handleResultsTabChange,
    // Config (now includes emailTemplate)
    wellnessConfig,
    setWellnessConfig,
    handleConfigChange,
    handleSaveConfig,
    isLoadingConfig,
    isSavingConfig,
    // Candidates — server-paginated and server-searched
    candidates,
    isLoadingCandidates,
    isSendingInvitation,
    handleSendInvitation,
    handleSendReminder,
    candidateSearch,
    setCandidateSearch,
    candidateEmailSearch,
    setCandidateEmailSearch,
    assessmentStatusFilter,
    setAssessmentStatusFilter,
    candidatePagination: {
      currentPage: candidatePage,
      totalPages: Math.max(1, Math.ceil(candidateTotalCount / PAGE_SIZE)),
      totalRecords: candidateTotalCount,
      onPageChange: setCandidatePage,
    },
    // Results
    summaryResults,
    isLoadingResults,
    candidateResults: pagedResults,
    resultPagination: {
      currentPage: resultPage,
      totalPages: resultTotalPages,
      totalRecords: candidateResults.length,
      onPageChange: setResultPage,
    },
  } as const;
}